import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { CARD_TYPES } from '../../../shared/constants';
import { IImage } from '../../../types/shared';

import mq from '../../../styles/breakpoints';
import Colors from '../../../styles/colors';
import { H1, H2 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

interface Props {
  image?: IImage;
  title: string;
  subtitle?: string;
}

const SubhomeTopComponent = ({ image, title, subtitle }: Props) => {
  return (
    <Container id={CARD_TYPES.subhomeTopComponent}>
      <ImageContainer>
        <ColorBackground>
          <InnerTextWrapper>
            {title && <StyledH1>{title}</StyledH1>}
            {subtitle && <StyledH2>{subtitle}</StyledH2>}
          </InnerTextWrapper>
        </ColorBackground>
        {image ? (
          <DesktopImage
            alt={image.description || ''}
            image={image.gatsbyImageData}
            objectPosition="50% 50%"
          />
        ) : (
          <FallbackBackground />
        )}
      </ImageContainer>
    </Container>
  );
};

export default SubhomeTopComponent;

const Container = styled.div`
  background: ${Colors.white};
  background-color: rgb(255, 255, 255);
  box-shadow: rgb(34 34 34 / 2%) 0px 0px 6px -4px,
    rgb(34 34 34 / 15%) 0px 4px 8px -4px;
  box-sizing: border-box;
  position: relative;
`;

const ImageContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  position: relative;
  width: 100%;

  ${mq.from.breakpoint.L`
    grid-template-columns: 1fr 1fr;
  `}
`;

const ColorBackground = styled.div`
  background-color: ${({ theme }) => theme.colors.abnGreen500};
  color: ${({ theme }) => theme.colors.abnNeutralWhite};
  display: flex;
  flex-direction: column;
  grid-row: 2;
  padding: ${Spacings.S} ${Spacings.S} ${Spacings.XL};

  ${mq.from.breakpoint.M`
    padding: ${Spacings.L} ${Spacings.M} ${Spacings.XXXL};
  `}

  ${mq.from.breakpoint.L`
    padding: 0;
    grid-row: none;
    grid-column: 1;
    justify-content: center;
  `}
`;

const InnerTextWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0;

  ${mq.from.breakpoint.L`
    padding: 0 ${Spacings.XXXL} 0 84px;
  `}

  ${mq.from.breakpoint.XL`
    padding: 0;
    margin: 0 auto;
    max-width: 572px;
  `}
`;

const StyledH1 = styled(H1)`
  margin: 0;
  word-break: break-word;
  word-wrap: break-word;
`;

const StyledH2 = styled(H2)`
  margin: ${Spacings.XS} 0 0;
`;

const DesktopImage = styled(GatsbyImage)`
  display: block;
  grid-row: 1;
  height: 260px;
  position: relative;

  ${mq.from.breakpoint.M`
    height: 432px;
  `}

  ${mq.from.breakpoint.L`
    grid-row: none;
    grid-column: 2;
  `}

  &:after {
    background: linear-gradient(
      79.33deg,
      rgba(0, 0, 0, 0.5) 0.58%,
      rgba(0, 0, 0, 0.36) 18.66%,
      rgba(255, 255, 255, 5e-5) 57.93%
    );
    background-blend-mode: multiply, normal;
    bottom: 0%;
    content: '';
    display: block;
    height: 100%;
    left: 0%;
    position: absolute;
    right: 0%;
    top: 0%;
    width: 100%;
  }
`;

const FallbackBackground = styled.div`
  background: ${Colors.abnGradient};
  height: auto;
  min-height: 160px;

  ${mq.from.breakpoint.M`
    min-height: 440px;
  `}
`;
